import React, { useMemo, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { LessonVideo } from './LessonVideo';
import { LessonQuiz } from './LessonQuiz';
import { LessonGame } from './LessonGame';

import { Course } from '../../types/course';
import { UserCourse } from '../../types/userCourse';
import { Certification } from '../../types/certification';
import { useIonRouter } from '@ionic/react';
import { useUserCourseContext } from '../../contexts/UserCourseContext';

type CurrentLesson = {
  type: 'video' | 'quiz' | 'game';
  order?: number; // Only for videos
} | null;

interface LessonProps {
  slug: string;
  course: Course;
  userCourse: UserCourse | null;
  currentLesson: CurrentLesson;
  certification?: Certification;
  onUserCourseUpdate?: (userCourse: UserCourse) => void;
  onNavigateToLesson: (lesson: CurrentLesson) => void;
  onToggleSidebar?: () => void;
}

export const Lesson: React.FC<LessonProps> = ({ slug, course, userCourse, currentLesson, certification, onUserCourseUpdate, onNavigateToLesson, onToggleSidebar }) => {
  const router = useIonRouter();
  const history = useHistory();
  const { setCurrentLessonType } = useUserCourseContext();

  // Determine which lesson to actually show
  const lessonToShow = useMemo(() => {
    // If currentLesson is set, use it
    if (currentLesson) {
      return currentLesson;
    }

    // Otherwise, determine first lesson
    if (!userCourse) return null;

    // No progress - determine first available lesson
    if (course.videos && course.videos.length > 0) {
      return { type: 'video' as const, order: 1 };
    }

    if (course.quizId && course.quizTitle) {
      return { type: 'quiz' as const };
    }

    if (course.gameId && course.gameTitle) {
      return { type: 'game' as const };
    }

    return null;
  }, [currentLesson, userCourse, course]);

  // Sync current lesson type to context so TestPanelDesktopSidebar can read it
  useEffect(() => {
    setCurrentLessonType(lessonToShow?.type ?? null);
  }, [lessonToShow, setCurrentLessonType]);

  // Calculate next lesson or next course route
  const getNextLesson = useCallback((): CurrentLesson | 'nextCourse' | null => {
    if (!lessonToShow) return null;

    const { type, order } = lessonToShow;

    if (type === 'video') {
      const totalVideos = course.videos?.length || 0;
      if (order && order < totalVideos) {
        return { type: 'video', order: order + 1 };
      }
      // No more videos - check quiz
      if (course.quizId && course.quizTitle) {
        return { type: 'quiz' };
      }
      if (course.gameId && course.gameTitle) {
        return { type: 'game' };
      }
      return 'nextCourse';
    }

    if (type === 'quiz') {
      if (course.gameId && course.gameTitle) {
        return { type: 'game' };
      }
      return 'nextCourse';
    }

    if (type === 'game') {
      return 'nextCourse';
    }

    return null;
  }, [lessonToShow, course]);

  // Get next course route when needed
  const getNextCourseRoute = useCallback((): string | null => {
    if (!certification) return null;

    // Find current course category and position
    let currentCategoryIndex = -1;
    let currentCourseIndex = -1;

    certification.courseCategories
      .sort((a, b) => a.order - b.order)
      .forEach((category, catIdx) => {
        const courseIdx = category.courses
          .sort((a, b) => a.categoryOrder - b.categoryOrder)
          .findIndex(c => c.id === course.id);

        if (courseIdx !== -1) {
          currentCategoryIndex = catIdx;
          currentCourseIndex = courseIdx;
        }
      });

    if (currentCategoryIndex !== -1) {
      const sortedCategories = certification.courseCategories.sort((a, b) => a.order - b.order);
      const currentCategory = sortedCategories[currentCategoryIndex];
      const sortedCourses = currentCategory.courses.sort((a, b) => a.categoryOrder - b.categoryOrder);

      // Try to find next course in same category
      if (currentCourseIndex < sortedCourses.length - 1) {
        const nextCourse = sortedCourses[currentCourseIndex + 1];
        return certification.id
          ? `/course/${nextCourse.slug}?certificationId=${certification.id}`
          : `/course/${nextCourse.slug}`;
      }

      // Try to find first course in next category
      if (currentCategoryIndex < sortedCategories.length - 1) {
        const nextCategory = sortedCategories[currentCategoryIndex + 1];
        if (nextCategory.courses.length > 0) {
          const firstCourseInNextCategory = nextCategory.courses.sort((a, b) => a.categoryOrder - b.categoryOrder)[0];
          return certification.id
            ? `/course/${firstCourseInNextCategory.slug}?certificationId=${certification.id}`
            : `/course/${firstCourseInNextCategory.slug}`;
        }
      }
    }

    // No next course found - certification completed
    return null;
  }, [certification, course.id]);

  // Calculate previous lesson
  const getPreviousLesson = useCallback((): CurrentLesson | 'allCourses' => {
    if (!lessonToShow) return 'allCourses';

    const { type, order } = lessonToShow;

    if (type === 'video') {
      // If we're on video 1, go back to all courses
      if (order === 1) {
        return 'allCourses';
      }
      // Go to previous video
      if (order && order > 1) {
        return { type: 'video', order: order - 1 };
      }
      return 'allCourses';
    }

    if (type === 'quiz') {
      const totalVideos = course.videos?.length || 0;
      // If there are videos, go to the last video
      if (totalVideos > 0) {
        return { type: 'video', order: totalVideos };
      }
      // Otherwise, go back to all courses
      return 'allCourses';
    }

    if (type === 'game') {
      // If there's a quiz, go to it
      if (course.quizId && course.quizTitle) {
        return { type: 'quiz' };
      }
      // If there are videos, go to the last video
      const totalVideos = course.videos?.length || 0;
      if (totalVideos > 0) {
        return { type: 'video', order: totalVideos };
      }
      // Otherwise, go back to all courses
      return 'allCourses';
    }

    return 'allCourses';
  }, [lessonToShow, course]);

  // Handle lesson completion and navigate to next lesson
  const handleProgressToNext = useCallback(() => {
    const next = getNextLesson();
    if (next === 'nextCourse') {
      const nextCourseRoute = getNextCourseRoute();
      if (nextCourseRoute) {
        router.push(nextCourseRoute, 'none');
      } else {
        router.push('/all-courses', 'none');
      }
    } else if (next) {
      onNavigateToLesson(next);
    }
  }, [getNextLesson, getNextCourseRoute, onNavigateToLesson, router]);

  // Handle navigation to previous lesson
  const handleNavigateToPrevious = useCallback(() => {
    const previous = getPreviousLesson();
    if (previous === 'allCourses') {
      router.push('/all-courses', 'none');
    } else {
      onNavigateToLesson(previous);
    }
  }, [getPreviousLesson, onNavigateToLesson, router]);

  // Handle navigation to next lesson (for button click)
  const handleNavigateToNext = useCallback(() => {
    const next = getNextLesson();
    if (next === 'nextCourse') {
      const nextCourseRoute = getNextCourseRoute();
      if (nextCourseRoute) {
        router.push(nextCourseRoute, 'none');
      } else {
        router.push('/all-courses', 'none');
      }
    } else if (next) {
      onNavigateToLesson(next);
    }
  }, [getNextLesson, getNextCourseRoute, onNavigateToLesson, router]);

  // Label for "next step" when the next step is next course
  const nextStepLabel = getNextLesson() === 'nextCourse'
    ? (getNextCourseRoute() ? 'Next Course' : 'Return')
    : undefined;

  // Calculate step number and total steps for UI
  const stepInfo = useMemo(() => {
    if (!lessonToShow) return { stepNumber: 1, totalSteps: 1 };

    const totalVideos = course.videos?.length || 0;
    const totalSteps = course.steps;

    let stepNumber = 1;
    if (lessonToShow.type === 'video' && lessonToShow.order) {
      stepNumber = lessonToShow.order;
    } else if (lessonToShow.type === 'quiz') {
      stepNumber = totalVideos + 1;
    } else if (lessonToShow.type === 'game') {
      stepNumber = totalVideos + (course.quizId ? 2 : 1);
    }

    return { stepNumber, totalSteps };
  }, [lessonToShow, course]);

  // Check if next/previous lessons exist
  const hasNextLesson = getNextLesson() !== null;
  const hasPreviousLesson = true; // Always true since we can always go to allCourses

  // Don't render lesson content until userCourse is loaded
  // This ensures video position and progress data are available before initialization
  if (!userCourse || userCourse.courseId !== course.id) {
    return (
      <div className="flex-1 min-w-0 bg-white flex items-center justify-center">
        <p className="text-gray-500">Loading course progress...</p>
      </div>
    );
  }

  // Determine which lesson component to render based on lessonToShow
  const renderLesson = () => {
    if (!lessonToShow) return null;

    switch (lessonToShow.type) {
      case 'video':
        return (
          <LessonVideo
            course={course}
            userCourse={userCourse}
            onProgressUpdate={onUserCourseUpdate}
            progressToNext={handleProgressToNext}
            onToggleSidebar={onToggleSidebar}
            hasNextLesson={hasNextLesson}
            hasPreviousLesson={hasPreviousLesson}
            onNavigateToNext={handleNavigateToNext}
            onNavigateToPrevious={handleNavigateToPrevious}
            stepNumber={stepInfo.stepNumber}
            totalSteps={stepInfo.totalSteps}
            nextStepLabel={nextStepLabel}
          />
        );

      case 'quiz':
        return (
          <LessonQuiz
            course={course}
            userCourse={userCourse}
            onProgressUpdate={onUserCourseUpdate}
            progressToNext={handleProgressToNext}
            onToggleSidebar={onToggleSidebar}
            hasNextLesson={hasNextLesson}
            hasPreviousLesson={hasPreviousLesson}
            onNavigateToNext={handleNavigateToNext}
            onNavigateToPrevious={handleNavigateToPrevious}
            stepNumber={stepInfo.stepNumber}
            totalSteps={stepInfo.totalSteps}
            nextStepLabel={nextStepLabel}
          />
        );

      case 'game':
        return (
          <LessonGame
            course={course}
            userCourse={userCourse}
            onProgressUpdate={onUserCourseUpdate}
            progressToNext={handleProgressToNext}
            onToggleSidebar={onToggleSidebar}
            hasNextLesson={hasNextLesson}
            hasPreviousLesson={hasPreviousLesson}
            onNavigateToNext={handleNavigateToNext}
            onNavigateToPrevious={handleNavigateToPrevious}
            stepNumber={stepInfo.stepNumber}
            totalSteps={stepInfo.totalSteps}
            nextStepLabel={nextStepLabel}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div
      className="flex-1 min-w-0 bg-white"
      {...(slug === 'introduction-video' ? { 'data-tour': 'welcome-course-player' } : {})}
    >
      {renderLesson()}
    </div>
  );
};
